import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, Phone, CheckCircle } from 'lucide-react';
import Seo from '../components/common/Seo';
import PageHero from '../components/common/PageHero';
import { services } from '@/data/services';
import { company } from '@/data/company';
import { contact } from '@/data/contact';
import { seo } from '@/data/seo';
import { getIcon } from '@/components/common/iconMap';

const Services = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      <Seo {...seo.services} />
      <PageHero
        eyebrow="Our services"
        title="Engineering and industrial"
        highlight="service lines"
        description="MAYAN supplies, installs, commissions, and maintains specialised equipment and provides economic, industrial, and technical consultation."
      />

      <section className="py-20 relative bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = getIcon(service.icon);
              return (
                <motion.article
                  key={service.slug}
                  id={service.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:border-[#398024] hover:shadow-2xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-[#398024] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Icon className="text-white" size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-[#398024] transition-colors">
                    {service.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <img
              src="/assets/content/oil-gas.jpg"
              alt="Oil and gas industrial facility"
              width={1920}
              height={1200}
              className="w-full rounded-3xl border border-gray-200 object-cover"
              loading="lazy"
            />
            <div>
              <h2 className="text-sm font-semibold text-[#398024] tracking-wider uppercase mb-3">Also on our profile</h2>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">{company.electromechanical.title}</h3>
              <p className="text-gray-600 leading-relaxed mb-6">{company.electromechanical.description}</p>
              <ul className="space-y-3">
                {company.electromechanical.items.map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <CheckCircle className="text-[#398024] flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 relative bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#398024]/10 to-[#398024]/5 border border-[#398024]/20 rounded-3xl p-12 text-center">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Need a tailored scope of work?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto text-lg">
              Tell us about the plant, study, or equipment requirement and we will respond from Amman.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-[#398024] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#2d6b1a] hover:shadow-xl transition-all duration-300"
              >
                <Phone size={20} className="mr-2" />
                Contact Us
              </Link>
              <a
                href={`mailto:${contact.email}`}
                className="inline-flex items-center justify-center bg-white border-2 border-gray-200 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:border-[#398024] hover:text-[#398024] transition-all duration-300"
              >
                <Mail size={20} className="mr-2" />
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
